import React, { useState, useEffect, useCallback } from "react";
import { getApiToken, setApiToken, clearApiToken } from "../lib/authToken";

/**
 * RequireCrmAuth — Garde d'authentification pour les outils internes.
 *
 * Vérifie qu'un JWT valide (scope "crm") est présent avant d'afficher le
 * contenu protégé. Sinon, affiche un formulaire de connexion qui appelle
 * /api/crm-auth/login (vérification serveur contre la table crm_users).
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function RequireCrmAuth({ children }) {
  const [status, setStatus] = useState("checking"); // checking | authed | anon
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const verify = useCallback(async () => {
    const token = getApiToken();
    if (!token) {
      setStatus("anon");
      return;
    }
    try {
      const r = await fetch(`${API}/api/crm-auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setStatus("authed");
      } else {
        clearApiToken();
        setStatus("anon");
      }
    } catch {
      // Réseau indisponible : on n'écrase pas un éventuel jeton valide.
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/crm-auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim(), password }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.token) {
        setApiToken(data.token);
        setStatus("authed");
      } else {
        setError(typeof data.detail === "string" ? data.detail : "Identifiants incorrects");
      }
    } catch {
      setError("Serveur injoignable. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <div
        data-testid="crm-auth-checking"
        style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0B1220", color: "#93A4C3" }}
      >
        Vérification de la session…
      </div>
    );
  }

  if (status === "authed") return children;

  return (
    <div
      data-testid="crm-auth-gate"
      style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0B1220", padding: 24 }}
    >
      <form
        onSubmit={handleSubmit}
        style={{ width: "100%", maxWidth: 380, background: "#111C33", border: "1px solid #1E2E4D", borderRadius: 16, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,.45)" }}
      >
        <div style={{ fontSize: 12, letterSpacing: 2, color: "#E8600A", fontWeight: 700, textTransform: "uppercase" }}>
          Espace interne
        </div>
        <h1 style={{ color: "#F1F5FB", fontSize: 22, margin: "6px 0 20px", fontWeight: 700 }}>
          Connexion requise
        </h1>
        <label style={{ display: "block", color: "#93A4C3", fontSize: 13, marginBottom: 6 }}>Identifiant</label>
        <input
          data-testid="crm-auth-login-input"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          autoFocus
          style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #263a5f", background: "#0B1220", color: "#F1F5FB", marginBottom: 14, fontSize: 15 }}
        />
        <label style={{ display: "block", color: "#93A4C3", fontSize: 13, marginBottom: 6 }}>Mot de passe</label>
        <input
          data-testid="crm-auth-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #263a5f", background: "#0B1220", color: "#F1F5FB", marginBottom: 18, fontSize: 15 }}
        />
        {error && (
          <div data-testid="crm-auth-error" style={{ color: "#FCA5A5", fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}
        <button
          data-testid="crm-auth-submit"
          type="submit"
          disabled={submitting || !login || !password}
          style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "none", background: submitting ? "#7a3d0f" : "#E8600A", color: "#fff", fontWeight: 700, fontSize: 15, cursor: submitting ? "default" : "pointer" }}
        >
          {submitting ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </div>
  );
}
